import { useMemo, useRef, useState } from "react";
import { accountService, taskService } from "@miplatform/api";
import { HTTP_STATUSES, ISSUE_STATUSES } from "@miplatform/constants";
import { useBoolean } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import { createUUID, externalReferenceId } from "@miplatform/utils";
import { PREBILL_UNCATEGORIZED_TYPE } from "./prebillConstants";

export const useCloseoutPrebillViewModel = ({
  stateManager,
  issueIndex,
  attempt,
  getAttempt,
  onCancel,
}) => {
  const isLoading = stateManager.isBusy();
  const invoiceRef = useRef(null);
  const creatingInvoicesRef = useRef({});
  const issues = attempt.unfilteredIssues || attempt.issues;
  const [selectedSubItem, setSelectedSubItem] = useState(null);
  const [invoices, setInvoices] = useState(
    Object.fromEntries(issues.map((issue, i) => [i, issue.invoiceItem]))
  );
  const [laborRates, setLaborRates] = useState([]);
  const [billingCommitmentsTotal, setBillingCommitmentsTotal] = useState(0);
  const [isSummary, handleSummaryOpen, handleSummaryClose] = useBoolean();
  const currentIssue = useMemo(() => issues[issueIndex], [issues, issueIndex]);
  const currentInvoice = useMemo(
    () => invoices[issueIndex] || { subItems: [] },
    [invoices, issueIndex]
  );
  const isIncompleteIssues = useMemo(
    () => issues.some((issue) => issue.status === ISSUE_STATUSES.CONFIRMED),
    [issues]
  );
  const isNextDisabled = useMemo(
    () =>
      isSummary
        ? isIncompleteIssues
        : currentInvoice?.subItems?.length === 0 ||
          currentInvoice?.subItems?.some(
            (item) => item.isSubmitting || item.isNew
          ),
    [isSummary, isIncompleteIssues, currentInvoice]
  );
  const { totalPrice, variance } = useMemo(() => {
    if (!isSummary) {
      return { totalPrice: 0, variance: 0 };
    }
    const totalPrice = Object.values(invoices).reduce(
      (acc, curr) => acc + (curr?.totalPrice || 0),
      0
    );
    const variance = billingCommitmentsTotal - totalPrice;

    return { totalPrice, variance };
  }, [isSummary, invoices, billingCommitmentsTotal]);

  const getLaborRates = () =>
    stateManager.run(async () => {
      const response = await accountService.laborRates.getAll();
      if (response.status === HTTP_STATUSES.OK) {
        setLaborRates(
          response.data.map((rate) => ({
            value: rate.name,
            label: rate.name,
            rate: rate.rate,
          }))
        );
      }
    });

  const preBillInvoice = () => {
    const invoicesTotal = Object.values(invoices).reduce(
      (acc, curr) => acc + (curr.totalPrice || 0),
      0
    );
    if (Math.abs(billingCommitmentsTotal - invoicesTotal) > 0.01) {
      toastService.error({
        text: "The payments total does not match the invoice total. Please adjust accordingly.",
      });
      return;
    }
    stateManager.run(async () => {
      const orderResponse = await taskService.order.getById({
        orderId: attempt.orderId,
      });

      if (orderResponse.status === HTTP_STATUSES.OK) {
        const { data: order } = orderResponse;

        const response = await taskService.order.preBillInvoices({
          orderId: attempt.orderId,
          modifiedOn: order.modifiedOn,
          toastMessageConfig: {
            success: {
              text: "Order prebilled successfully.",
            },
          },
        });

        if (response.status === HTTP_STATUSES.NO_CONTENT) {
          getAttempt();
          onCancel();
        }
      }
    });
  };

  const getInvoices = () =>
    stateManager.run(async () =>
      accountService.invoices.getAll({
        filters: { orderId: { type: "guid", value: attempt?.orderId } },
      })
    );

  const createInvoice = async (id) =>
    stateManager.run(async () =>
      accountService.invoices.create({
        data: {
          id,
          orderId: attempt?.orderId,
          name: attempt?.assignment?.assignmentTemplateName,
        },
      })
    );

  const getInvoiceItem = async (id) =>
    stateManager.run(async () => accountService.invoiceItems.getById({ id }));

  const createInvoiceItem = async ({ data }) =>
    stateManager.run(async () => accountService.invoiceItems.create({ data }));

  const updateInvoiceSubItem = async ({ invoiceItemId, data }) =>
    stateManager.run(async () =>
      accountService.invoiceSubItems.upsert({
        invoiceItemId,
        data,
        toastMessageConfig: {
          success: {
            text: "Invoice item updated successfully.",
          },
        },
      })
    );

  const deleteInvoiceSubItem = async ({ id, modifiedOn }) =>
    stateManager.run(async () =>
      accountService.invoiceSubItems.delete({
        id,
        modifiedOn,
        toastMessageConfig: {
          success: {
            text: "Invoice item deleted successfully.",
          },
        },
      })
    );

  const toggleItemLoading = (id, index) => {
    setInvoices((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        subItems: prev[index].subItems.map((item) =>
          item.id === id ? { ...item, isSubmitting: !item.isSubmitting } : item
        ),
      },
    }));
  };

  const handleAddSubItem = () => {
    const id = createUUID();
    const newItem = {
      id,
      isNew: true,
      type: PREBILL_UNCATEGORIZED_TYPE,
      name: "",
      quantity: "1",
      unitPrice: "",
    };
    setInvoices((prev) => ({
      ...prev,
      [issueIndex]: {
        ...prev[issueIndex],
        subItems: [...(prev[issueIndex]?.subItems || []), newItem],
      },
    }));
  };

  const handleUpdateSubItem = async (updatedItem, index) => {
    setInvoices((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        subItems: prev[index].subItems.map((item) =>
          item.id === updatedItem.id ? { ...item, isSubmitting: true } : item
        ),
      },
    }));

    const response = await updateInvoiceSubItem({
      invoiceItemId: invoices[index].id,
      data: {
        ...updatedItem,
        externalReferenceId: externalReferenceId.getIssueRefId({
          issueId: issues[index].id,
        }),
        modifiedOn: invoices[index].modifiedOn,
      },
    });

    if (response.data?.id) {
      if (
        invoices[index].subItems.length === 1 &&
        invoices[index].subItems[0].isNew
      ) {
        getAttempt();
      }

      setInvoices((prev) => ({
        ...prev,
        [index]: {
          ...response.data,
          subItems: [
            ...prev[index].subItems.filter(
              (item) => item.id !== updatedItem.id
            ),
            response.data.subItems.find(
              (subItem) => subItem.id === updatedItem.id
            ),
          ],
        },
      }));

      return true;
    }

    toggleItemLoading(updatedItem.id, index);

    return false;
  };

  const handleDeleteSubItem = async () => {
    toggleItemLoading(selectedSubItem.item.id, selectedSubItem.index);

    const response = await deleteInvoiceSubItem({
      id: selectedSubItem?.item?.id,
      modifiedOn: invoices[selectedSubItem.index]?.modifiedOn,
    });

    if (response.status === HTTP_STATUSES.OK) {
      setInvoices((prev) => ({
        ...prev,
        [selectedSubItem.index]: {
          ...prev[selectedSubItem.index],
          subItems: [
            ...prev[selectedSubItem.index].subItems.filter(
              (item) => item.id !== selectedSubItem?.item?.id
            ),
          ],
        },
      }));

      handleDeleteModalClose();

      const response = await getInvoiceItem(invoices[selectedSubItem.index].id);

      if (response.data?.id) {
        setInvoices((prev) => ({
          ...prev,
          [selectedSubItem.index]: {
            ...response.data,
            subItems: prev[selectedSubItem.index].subItems,
          },
        }));
      }
    } else {
      toggleItemLoading(selectedSubItem.item.id, selectedSubItem.index);
      handleDeleteModalClose();
    }
  };

  const handleGetOrCreateInvoice = async () => {
    const response = await getInvoices();

    if (response.data?.length > 0) {
      const [firstInvoice] = response.data;

      invoiceRef.current = firstInvoice;
      return firstInvoice;
    }

    invoiceRef.current = { id: createUUID() };
    const createResponse = await createInvoice(invoiceRef.current.id);

    if (createResponse.data?.id) {
      invoiceRef.current = createResponse.data;
      return createResponse.data;
    }

    return invoiceRef.current;
  };

  const handleCreateInvoiceItem = async () => {
    if (creatingInvoicesRef.current[issueIndex]) {
      return;
    }

    creatingInvoicesRef.current[issueIndex] = true;
    let invoiceId = invoiceRef.current?.id;

    if (!invoiceId) {
      const invoice = await handleGetOrCreateInvoice();
      invoiceId = invoice?.id;
    }

    const response = await createInvoiceItem({
      data: {
        invoiceId,
        section: "",
        name: currentIssue.title,
        quantity: 1,
        externalReferenceId: externalReferenceId.getIssueRefId({
          issueId: currentIssue.id,
        }),
      },
    });
    creatingInvoicesRef.current[issueIndex] = false;

    if (response?.data?.id) {
      setInvoices((prev) => ({
        ...prev,
        [issueIndex]: {
          ...response.data,
        },
      }));
    }

    getAttempt();
  };

  const handleDeleteModalOpen = (invoiceSubItem, index) => {
    if (invoiceSubItem?.isNew) {
      setInvoices((prev) => ({
        ...prev,
        [index]: {
          ...prev[index],
          subItems: prev[index].subItems.filter(
            (item) => item.id !== invoiceSubItem?.id
          ),
        },
      }));
      return;
    }

    setSelectedSubItem({ item: invoiceSubItem, index });
  };

  const handleDeleteModalClose = () => setSelectedSubItem(null);

  return {
    isLoading,
    isSummary,
    isNextDisabled,
    invoiceRef,
    invoices,
    invoice: invoiceRef.current,
    currentIssue,
    currentInvoice,
    selectedSubItem,
    laborRates,
    billingCommitmentsTotal,
    totalPrice,
    variance,
    isIncompleteIssues,
    issues,
    setBillingCommitmentsTotal,
    getLaborRates,
    preBillInvoice,
    handleAddSubItem,
    handleUpdateSubItem,
    handleDeleteSubItem,
    handleCreateInvoiceItem,
    handleDeleteModalOpen,
    handleDeleteModalClose,
    handleGetOrCreateInvoice,
    handleSummaryOpen,
    handleSummaryClose,
  };
};
